import Vue from 'vue';
import EventBus from '@/EventBus';
import ApplicationEventTypes from '@/mixins/ApplicationEventTypes';
import LayerContextMenu from '@/components/Layer/LayerContextMenu.vue';
import {
    canGroupObjects, canAddLayersToGroup, forEachCanvasObject, moveSelectedInStack,
} from '@/components/Canvas/utils/groupHelpers';

// Same cases as groupObjects(): a new group from the selected layers, or the selected layers added to a selected group.
function canGroupSelection(vm) {
    const selected = vm.objectList.filter(object => object.isSelected);
    const layers   = selected.filter(object => object.type !== 'group');
    if (selected.length < 2) { return false; }
    return selected.length > layers.length ? canAddLayersToGroup(layers) : canGroupObjects(layers);
}

// Same check as ungroupObjects(): a whole group is selected.
function hasSelectedGroup(vm) {
    return vm.objectList.some(object => object.isSelected && object.type === 'group');
}

// Selected elements inside a group (the group itself stays unselected).
function getSelectedGroupChildren(vm) {
    const children = [];
    vm.objectList
        .filter(object => object.type === 'group')
        .forEach(group => forEachCanvasObject(group.children, (child) => {
            if (child.isSelected) { children.push(child); }
        }));
    return children;
}

// Display labels for the shortcuts handled in Canvas doCommand(), which accepts Cmd or Ctrl.
const IS_MAC = /Mac|iPhone|iPad/.test(navigator.platform);
const MOD    = IS_MAC ? '⌘' : 'Ctrl+';

const ALIGN_ACTIONS = [
    { value: 'left', label: 'Align Left', icon: 'alignleft' },
    { value: 'center', label: 'Align Center', icon: 'aligncenter' },
    { value: 'right', label: 'Align Right', icon: 'alignright' },
    { value: 'top', label: 'Align Top', icon: 'aligntop' },
    { value: 'middle', label: 'Align Middle', icon: 'alignmiddle' },
    { value: 'bottom', label: 'Align Bottom', icon: 'alignbottom' },
].map(({ value, label, icon }) => ({
    id:  `align-${value}`,
    label,
    icon,
    run: vm => vm.alignObjectPosition({ value, productCrop: vm.productCrop }),
}));

const LAYER_ACTIONS = [
    { direction: 'front', label: 'Bring to Front', icon: 'bringtofront' },
    { direction: 'forward', label: 'Bring Forward', icon: 'bringforward' },
    { direction: 'backward', label: 'Send Backward', icon: 'sendbackward' },
    { direction: 'back', label: 'Send to Back', icon: 'sendtoback' },
].map(({ direction, label, icon }) => ({
    id:  `layer-${direction}`,
    label,
    icon,
    run: vm => vm.moveSelectionInStack(direction),
}));

const ELEMENT_ACTIONS = [
    {
        id:          'copy',
        label:       'Copy',
        icon:        'copy',
        shortcut:    `${MOD}C`,
        isAvailable: () => true,
        run:         vm => vm.copyByKey('copy'),
    },
    {
        id:          'cut',
        label:       'Cut',
        icon:        'cut',
        shortcut:    `${MOD}X`,
        isAvailable: vm => vm.moveableObj.props.deleteHandle,
        run:         vm => vm.copyByKey('cut'),
    },
    {
        id:          'duplicate',
        label:       'Duplicate',
        icon:        'copied',
        isAvailable: vm => vm.moveableObj.props.duplicateHandle,
        run:         () => EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.OBJECT_DUPLICATE),
    },
    {
        id:          'crop',
        label:       'Crop',
        icon:        'cropimage',
        isAvailable: vm => vm.moveableObj.props.cropHandle,
        run:         () => EventBus.$emit(ApplicationEventTypes.ModalEvents.TOGGLE_CROP_IMAGE_MODAL),
    },
    {
        id:          'add-to-group',
        label:       'Add to Group',
        icon:        'folderIcon',
        isAvailable: canGroupSelection,
        run:         vm => vm.groupObjects(),
    },
    {
        id:          'ungroup',
        label:       'Ungroup',
        icon:        'removeFromGroupIcon',
        isAvailable: hasSelectedGroup,
        run:         vm => vm.ungroupObjects(),
    },
    {
        id:          'remove-from-group',
        label:       'Remove from group',
        icon:        'removeFromGroupIcon',
        isAvailable: vm => getSelectedGroupChildren(vm).length > 0,
        // Handled by the Layers panel's removeFromGroup(), which acts on the whole selection.
        run:         vm => EventBus.$emit(
            ApplicationEventTypes.ObjectSelectionEvents.REMOVE_FROM_GROUP,
            getSelectedGroupChildren(vm)[0],
        ),
    },
    {
        id:          'delete',
        label:       'Delete',
        icon:        'trash',
        shortcut:    'Del / ⌫',
        isAvailable: vm => vm.moveableObj.props.deleteHandle,
        run:         () => EventBus.$emit(ApplicationEventTypes.ModalEvents.TOGGLE_CONFIRMATION_MODAL, {
            request: 'DeleteReq',
            data:    -1,
            message: 'Are you sure you want to delete selected Layer(s)?',
            isVideo: true,
        }),
    },
    { id: 'divider-arrange', divider: true },
    {
        id:       'align-to-page',
        label:    'Position',
        icon:     'alignpage',
        children: ALIGN_ACTIONS,
    },
    {
        id:       'layer',
        label:    'Layer',
        icon:     'layers',
        children: LAYER_ACTIONS,
    },
];

const CANVAS_ACTIONS = [
    {
        id:          'paste',
        label:       'Paste',
        icon:        'paste',
        shortcut:    `${MOD}V`,
        isAvailable: () => true,
        isEnabled:   vm => (vm.$store.getters.getCopiedObjects || []).length > 0,
        run:         vm => vm.pasteCopiedObjects(),
    },
];

const TABLE_MENU_ACTION_IDS = ['copy', 'cut', 'duplicate', 'delete', 'divider-arrange', 'align-to-page', 'layer'];
const TABLE_MENU_ACTIONS    = ELEMENT_ACTIONS.filter(action => TABLE_MENU_ACTION_IDS.includes(action.id));

function flattenMenuItems(items) {
    return items.reduce((all, item) => all.concat(item, flattenMenuItems(item.children || [])), []);
}

export default {
    data() {
        return {
            contextMenu: {
                visible: false,
                x:       0,
                y:       0,
                items:   [],
            },
        };
    },

    methods: {
        initContextMenu() {
            // Mounted on <body>: the canvas is CSS-scaled, which would offset a fixed-position menu inside it.
            this.contextMenuHost = new Vue({
                parent: this,
                render: h => h(LayerContextMenu, {
                    props: {
                        visible: this.contextMenu.visible,
                        x:       this.contextMenu.x,
                        y:       this.contextMenu.y,
                        items:   this.contextMenu.items,
                    },
                    on: { select: this.onContextMenuSelect },
                }),
            }).$mount();
            document.body.appendChild(this.contextMenuHost.$el);

            // Listen on Moveable's container too, so right-clicks on the selection box are caught.
            this.container.parentNode.addEventListener('contextmenu', this.onCanvasContextMenu);
            document.addEventListener('mousedown', this.onContextMenuOutsideMouseDown, true);
            EventBus.$on(ApplicationEventTypes.Canvas.OPEN_ELEMENT_MENU, this.openTableMenu);
        },

        destroyContextMenu() {
            if (this.contextMenuHost) {
                this.contextMenuHost.$destroy();
                this.contextMenuHost.$el.remove();
            }
            if (this.container) {
                this.container.parentNode.removeEventListener('contextmenu', this.onCanvasContextMenu);
            }
            document.removeEventListener('mousedown', this.onContextMenuOutsideMouseDown, true);
            EventBus.$off(ApplicationEventTypes.Canvas.OPEN_ELEMENT_MENU, this.openTableMenu);
        },

        openTableMenu(buttonRect) {
            this.openContextMenu(buttonRect.left, buttonRect.bottom + 4, TABLE_MENU_ACTIONS);
        },

        onCanvasContextMenu(e) {
            const { target } = e;
            const isOnCanvas = this.container.contains(target) || this.moveableObj.isMoveableElement(target);
            // Tables open their own menu; text being edited keeps the browser menu.
            if (!isOnCanvas || e.defaultPrevented || target.closest('[contenteditable="true"]')) {
                return;
            }
            e.preventDefault();

            const object = this.getContextMenuObjectAtEvent(e);
            if (object && !object.isSelected) {
                this.selectingCanvasObjectFromRightPanel(object.Id);
            }

            // Wait for the selection to update the Moveable handle flags.
            this.$nextTick(() => {
                const actions = object ? ELEMENT_ACTIONS : CANVAS_ACTIONS;
                this.openContextMenu(e.clientX, e.clientY, actions);
            });
        },

        // The right-clicked element. Prefers one that is already selected, so a
        // selected group child or multi-selection is kept as is. Locked and hidden
        // elements can't be selected, so they count as empty canvas.
        getContextMenuObjectAtEvent(e) {
            // Only group children are hit (the group element has no size), so resolve
            // each one to what a left-click would select, e.g. its parent group.
            const objects = document.elementsFromPoint(e.clientX, e.clientY)
                .filter(el => el.classList.contains('target') && this.$el.contains(el))
                .map(el => this.findObj(el.id))
                .filter(Boolean)
                .map(object => (object.isSelected ? object : this.resolveTopLevelSelection(object, this.activeObject)))
                .filter(object => this.canTransformObject(object));

            return objects.find(object => object.isSelected) || objects[0] || null;
        },

        openContextMenu(x, y, actions) {
            const items = this.toContextMenuItems(actions);
            if (!items.length) { return; }

            this.contextMenu = {
                visible: true, x, y, items,
            };
            this.$nextTick(this.keepContextMenuInViewport);
        },

        toContextMenuItems(actions) {
            return actions
                .filter(action => !action.isAvailable || action.isAvailable(this))
                .map(action => ({
                    id:       action.id,
                    label:    action.label,
                    icon:     action.icon,
                    shortcut: action.shortcut,
                    divider:  action.divider,
                    run:      action.run,
                    disabled: action.isEnabled ? !action.isEnabled(this) : false,
                    children: action.children && this.toContextMenuItems(action.children),
                }));
        },

        moveSelectionInStack(direction) {
            moveSelectedInStack(this.objectList, direction);
            const selected = [];
            forEachCanvasObject(this.objectList, (object) => {
                if (object.isSelected) { selected.push(object); }
            });
            const anchorId = this.selectionAnchorId;
            EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.objectList);
            EventBus.$emit(ApplicationEventTypes.Canvas.CANVAS_RE_RENDER);
            // The re-render remounts the canvas, so restore the selection on the new instance.
            this.$nextTick(() => {
                if (selected.length > 1) {
                    EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.SELECT_MULTIPLE_OBJECTS, {
                        objects: selected,
                        anchorId,
                    });
                } else if (selected.length === 1) {
                    EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.SELECTED_FROM_RIGHT_PANEL, selected[0].Id);
                }
            });
            this.emitUndoRedoRequest(`called by layer ${direction}`);
        },

        keepContextMenuInViewport() {
            const rect = this.contextMenuHost.$el.getBoundingClientRect();
            if (rect.right > window.innerWidth) {
                this.contextMenu.x -= rect.right - window.innerWidth;
            }
            if (rect.bottom > window.innerHeight) {
                this.contextMenu.y -= rect.bottom - window.innerHeight;
            }
        },

        closeContextMenu() {
            this.contextMenu.visible = false;
        },

        onContextMenuSelect(actionId) {
            const item = flattenMenuItems(this.contextMenu.items).find(menuItem => menuItem.id === actionId);
            this.closeContextMenu();
            if (item && item.run) { item.run(this); }
        },

        onContextMenuOutsideMouseDown(e) {
            if (this.contextMenu.visible && !this.contextMenuHost.$el.contains(e.target)) {
                this.closeContextMenu();
            }
        },
    },
};
